import { DrawAdditionalPlayerInformation } from 'components/dialogs/draw/publicDrawDisplaySettings/types';
import { Category } from 'typedefs/event';

import { type ClientConfig, Feature } from './types';

const ustaConfig: ClientConfig = {
  defaultTheme: 'usta-light-theme',
  clientName: 'USTA',
  publicAssetsRoute: '/assets/usta',
  clientLogo: '/assets/usta/logo.svg',
  disableFeatures: [Feature.MULTI_PAIRED_PARTICIPANTS, Feature.VIRTUAL_CHECK_IN],
  publicSiteDisplayName: 'USTA.com',
  showOrganisationId: true,
  organisationId: '505916e7-d79c-4284-b6cf-23c3d1344d34',
  scorecardHomeDisplaySide: 2,
  defaultLocale: 'en-US',
  formats: {
    pickerDateFormat: 'MM/DD/YYYY',
    pickerTimeFormat: 'hh:mm a',
    pickerDateTimeFormat: 'MM/DD/YYYY hh:mm a',
  },
  additionalPlayerInfoOptions: [
    DrawAdditionalPlayerInformation.NONE,
    DrawAdditionalPlayerInformation.CITY_STATE,
    DrawAdditionalPlayerInformation.DISTRICT,
    DrawAdditionalPlayerInformation.SECTION,
    DrawAdditionalPlayerInformation.COUNTRY,
    DrawAdditionalPlayerInformation.TEAM,
  ],
  copyrightName: 'USTA',
  scalingTypes: [
    ['Ranking'],
    ['NTRP'],
    ['PT'],
    [
      'WTN',
      {
        confidenceBands: [
          { min: 0, color: '#d0d3db', description: 'Low degree' },
          { min: 40, color: '#6bb6e9', description: 'Medium degree' },
          { min: 70, color: '#003a70', description: 'High degree' },
        ],
      },
    ],
  ],
  mockPersonExtensions: {
    ustaSection: { name: 'Intermountain', code: '025' },
    ustaDistrict: { name: 'Montana', code: '025076' },
  },
  mockParticipants: [
    { firstName: 'Mike', lastName: 'Brown', sex: 'MALE', nationalityCode: 'USA' },
    { firstName: 'Mary', lastName: 'Jones', sex: 'FEMALE', nationalityCode: 'USA' },
  ],
  legacyAssistantSearch: true,
  courtsAvailableHours: 15,
  courtsDefaultMaxStartTime: '22:00',
  ageDivisions: {
    [Category.JUNIOR]: ['U8', 'U10', 'U12', 'U14', 'U16', 'U18'],
    [Category.ADULT]: [
      'OPEN',
      'O30',
      'O35',
      'O40',
      'O45',
      'O50',
      'O55',
      'O60',
      'O65',
      'O70',
      'O75',
      'O80',
      'O85',
      'O90',
      'O95',
      'O100',
    ],
    [Category.WHEELCHAIR]: ['OPEN', 'A', 'B', 'C', 'D', 'U18'],
    [Category.WTN_PLAY]: [],
  },
  statusCategories: [
    {
      label: 'None',
      subCategories: [
        {
          label: 'None',
          description: 'None',
          matchUpStatusCodeDisplay: 'None',
          matchUpStatusCode: '',
        },
      ],
    },
    {
      label: 'Winner',
      subCategories: [
        {
          label: 'Winner',
          description: 'Winner',
          matchUpStatusCodeDisplay: 'Winner',
          matchUpStatusCode: '',
        },
      ],
      hidden: true,
    },
    {
      label: 'Walkovers',
      subCategories: [
        {
          label: 'Injury',
          description: 'Injury',
          matchUpStatusCodeDisplay: 'Wo [inj]',
          matchUpStatusCode: 'W1',
        },
        {
          label: 'Illness',
          description: 'Illness',
          matchUpStatusCodeDisplay: 'Wo [ill]',
          matchUpStatusCode: 'W2',
        },
        {
          label: 'Personal circumstance',
          description: 'Personal circumstance',
          matchUpStatusCodeDisplay: 'Wo [pc]',
          matchUpStatusCode: 'W3',
        },
        {
          label: 'Double walkover',
          description: 'Double walkover',
          matchUpStatusCodeDisplay: 'Wo/Wo',
          matchUpStatusCode: 'WOWO',
        },
        {
          label: 'Tournament Administrative Error',
          description: 'Tournament Administrative Error',
          matchUpStatusCodeDisplay: 'Wo [Tae]',
          matchUpStatusCode: 'W4',
        },
        {
          label: 'Withdrawn',
          description: 'Withdrawn',
          matchUpStatusCodeDisplay: 'Wo/Withdrawn',
          matchUpStatusCode: 'W5',
        },
      ],
    },
    {
      label: 'Defaults',
      subCategories: [
        {
          label: 'Disqualification for cause or ineligibility',
          description: 'Disqualification (ineligibility)',
          matchUpStatusCodeDisplay: 'Def [dq]',
          matchUpStatusCode: 'DQ',
        },
        {
          label: 'Misconduct before or between matches',
          description: 'Misconduct',
          matchUpStatusCodeDisplay: 'Def [cond]',
          matchUpStatusCode: 'DM',
        },
        {
          label: 'Failure to start match because of adult discipline',
          description: 'Fail. (adult discipline)',
          matchUpStatusCodeDisplay: 'Def [ad]',
          matchUpStatusCode: 'D5',
        },
        {
          label:
            'Refusal to start match for reason other than adult discipline, injury, illness, or personal circumstance. ' +
            '(After the Referee has conclusively confirmed that a player refuses to play a match, the Referee need not ' +
            'wait until the scheduled time of the match to records the result.)',
          description: 'Refusal to start match',
          matchUpStatusCodeDisplay: 'Def [refsl]',
          matchUpStatusCode: 'D4',
        },
        {
          label: 'Not showing up',
          description: 'Not showing up',
          matchUpStatusCodeDisplay: 'Def [ns]',
          matchUpStatusCode: 'D6',
        },
        {
          label:
            'Lateness for match including, but not limited to, intending to play but mistakenly arriving at the ' +
            'wrong time, location, or without proper equipment',
          description: 'Lateness for match',
          matchUpStatusCodeDisplay: 'Score + Def [late]',
          matchUpStatusCode: 'D7',
        },
        {
          label: 'Double default',
          description: 'Double default',
          matchUpStatusCodeDisplay: 'Def/Def',
          matchUpStatusCode: 'DD',
        },
        {
          label:
            'Refusal to continue playing a match for reason other than injury, illness, personal circumstance, or ' +
            'adult discipline',
          description: 'Refusal to continue match',
          matchUpStatusCodeDisplay: 'Def [refsl]',
          matchUpStatusCode: 'D9',
        },
        {
          label: 'Default for receiving an injection, IV, or supplemental oxygen',
          description: 'Default (PEDs)',
          matchUpStatusCodeDisplay: 'Def [med]',
          matchUpStatusCode: 'DI',
        },
        {
          label: 'Default under Point Penalty System',
          description: 'Default (Point Penalty System)',
          matchUpStatusCodeDisplay: 'Def [pps]',
          matchUpStatusCode: 'DP',
        },
      ],
    },
    {
      label: 'Retirements',
      subCategories: [
        {
          label: 'Injury',
          description: 'Injury',
          matchUpStatusCodeDisplay: 'Ret [inj]',
          matchUpStatusCode: 'RJ',
        },
        {
          label: 'Illness',
          description: 'Illness',
          matchUpStatusCodeDisplay: 'Ret [ill]',
          matchUpStatusCode: 'RI',
        },
        {
          label: 'Personal circumstance',
          description: 'Personal circumstance',
          matchUpStatusCodeDisplay: 'Ret [pc]',
          matchUpStatusCode: 'RC',
        },
        {
          label: 'Retirement because of adult discipline',
          description: 'Ret. (adult discipline)',
          matchUpStatusCodeDisplay: 'Ret [ad]',
          matchUpStatusCode: 'RD',
        },
        {
          label:
            'A player who retires from a match remains eligible for consolations, place playoffs, doubles and ' +
            'subsequent round robin matches',
          description: 'Ret. (eligible)',
          matchUpStatusCodeDisplay: 'Ret [elg]',
          matchUpStatusCode: 'RU',
        },
      ],
    },
    {
      label: 'Other',
      subCategories: [
        {
          label: 'Incomplete match',
          description: 'Incomplete match',
          matchUpStatusCodeDisplay: 'Incomplete',
          matchUpStatusCode: 'OI',
        },
        {
          label: 'Abandoned match',
          description: 'Abandoned match',
          matchUpStatusCodeDisplay: 'Abandoned',
          matchUpStatusCode: 'OA',
        },
        {
          label: 'Cancelled match',
          description: 'Cancelled match',
          matchUpStatusCodeDisplay: 'Unplayed or Cancelled',
          matchUpStatusCode: 'OC',
        },
      ],
    },
    {
      label: 'Withdrawals',
      subCategories: [
        {
          label: 'Injury',
          description: 'Injury',
          matchUpStatusCodeDisplay: 'Wd [inj]',
          matchUpStatusCode: 'WD.INJ',
        },
        {
          label: 'Illness',
          description: 'Illness',
          matchUpStatusCodeDisplay: 'Wd [ill]',
          matchUpStatusCode: 'WD.ILL',
        },
        {
          label: 'Personal circumstance',
          description: 'Personal circumstance',
          matchUpStatusCodeDisplay: 'Wd [pc]',
          matchUpStatusCode: 'WD.PC',
        },
        {
          label: 'Double withdrawal',
          description: 'Double withdrawal',
          matchUpStatusCodeDisplay: 'Wd/Wd',
          matchUpStatusCode: 'WD.WD',
        },
        {
          label: 'Tournament Administrative Error',
          description: 'Tournament Administrative Error',
          matchUpStatusCodeDisplay: 'Wd [Tae]',
          matchUpStatusCode: 'WD.TAE',
        },
      ],
    },
  ],
};

export default ustaConfig;
